import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { allDemoHandlers } from './all-handlers';
import { DemoModeService } from './demo-mode.service';
import { DemoStore } from './store';
import {
  findRoute,
  type HandlerContext,
  type HandlerReturn,
  type HttpMethod,
} from './handler-registry';

const SIMULATED_LATENCY_MS = 60;

export const demoBackendInterceptor: HttpInterceptorFn = (req, next) => {
  const demo = inject(DemoModeService);
  if (!demo.isDemoActive()) return next(req);

  const parsed = parseUrl(req.url);
  if (!parsed || !parsed.path.startsWith('/api/')) return next(req);

  const store = inject(DemoStore);
  const method = req.method.toUpperCase() as HttpMethod;
  const match = findRoute(allDemoHandlers, method, parsed.path);

  if (!match) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 404,
          statusText: 'Not Found (demo)',
          url: req.url,
          error: { message: `No demo handler for ${method} ${parsed.path}` },
        }),
    );
  }

  const ctx: HandlerContext = {
    method,
    path: parsed.path,
    match: match.match,
    query: parsed.query,
    body: req.body ?? null,
    headers: collectHeaders(req.headers),
  };

  let output: HandlerReturn;
  try {
    output = match.entry.handle(ctx, store.snapshot());
  } catch (err) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 500,
          statusText: 'Demo handler error',
          url: req.url,
          error: { message: (err as Error).message },
        }),
    );
  }

  const { status, body } = resolveOutput(output, store);

  if (status >= 400) {
    return delayed<HttpEvent<unknown>>(
      throwError(
        () =>
          new HttpErrorResponse({
            status,
            statusText: 'Demo error',
            url: req.url,
            error: body,
          }),
      ),
    );
  }

  const response = new HttpResponse<unknown>({
    status,
    statusText: 'OK',
    url: req.url,
    body,
  });
  return delayed<HttpEvent<unknown>>(of(response));
};

function parseUrl(url: string): { path: string; query: URLSearchParams } | null {
  try {
    const base =
      typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
    const u = url.startsWith('http') ? new URL(url) : new URL(url, base);
    return { path: u.pathname, query: u.searchParams };
  } catch {
    return null;
  }
}

function collectHeaders(headers: {
  keys(): string[];
  get(name: string): string | null;
}): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of headers.keys()) {
    const value = headers.get(key);
    if (value !== null) out[key] = value;
  }
  return out;
}

function resolveOutput(
  output: HandlerReturn,
  store: DemoStore,
): { status: number; body: unknown } {
  if (output && typeof output === 'object') {
    const o = output as Record<string, unknown>;
    if ('error' in o && o['error'] && typeof o['error'] === 'object') {
      const err = o['error'] as { status?: number; message?: string };
      return {
        status: err.status ?? 500,
        body: { message: err.message ?? 'Error' },
      };
    }
    if ('mutate' in o && typeof o['mutate'] === 'function') {
      let result: unknown = null;
      store.update((data) => {
        const mutation = (o['mutate'] as (d: unknown) => { next: unknown; result: unknown })(data);
        result = mutation.result;
        return mutation.next as never;
      });
      if (result && typeof result === 'object' && 'error' in (result as object)) {
        const err = (result as { error: { status?: number; message?: string } }).error;
        return { status: err.status ?? 500, body: { message: err.message ?? 'Error' } };
      }
      return { status: 200, body: result ?? null };
    }
    if ('status' in o && 'body' in o && typeof o['status'] === 'number') {
      return { status: o['status'] as number, body: o['body'] };
    }
  }
  return { status: 200, body: output ?? null };
}

function delayed<T>(source: Observable<T>): Observable<T> {
  if (SIMULATED_LATENCY_MS <= 0) return source;
  return new Observable<T>((subscriber) => {
    const handle = setTimeout(() => {
      source.subscribe({
        next: (v) => subscriber.next(v),
        error: (e) => subscriber.error(e),
        complete: () => subscriber.complete(),
      });
    }, SIMULATED_LATENCY_MS);
    return () => clearTimeout(handle);
  });
}
